import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InfoTooltip from '../../../components/ui/Tooltip';
import propertyService from '../../../services/propertyService';
import { stripeService } from '../../../services/stripe';
import usageTrackingService from '../../../services/usageTracking';
import { calculateMortgagePayment } from '../../../utils/mortgageCalculations';
import DataValidationAlert, { validatePropertyData } from './DataValidationAlert';

const PropertyAIAnalysis = ({ property, user, userProfile, subscriptionTier = 'free', onEdit, analysis: externalAnalysis, isReadOnly = false }) => {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(externalAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(!externalAnalysis);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(!!externalAnalysis);
  const [usageStats, setUsageStats] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    secteur: true,
    prix: true,
    rentabilite: true,
    recommandation: true
  });

  useEffect(() => {
    if (externalAnalysis) {
      setAnalysis(externalAnalysis);
      setIsCached(true);
      setLoadingExisting(false);
    }
  }, [externalAnalysis]);

  // Load existing analysis on mount (skip if external analysis or read-only visitor)
  useEffect(() => {
    if (externalAnalysis || isReadOnly || !property?.id || !user?.id) {
      setLoadingExisting(false);
      return;
    }

    const loadExistingAnalysis = async () => {
      try {
        const result = await propertyService.getPropertyAIAnalysis(property.id, user.id);
        if (result?.success && result.data) {
          setAnalysis(result.data);
          setIsCached(true);
        }
      } catch (err) {
        // Silently handle errors
      } finally {
        setLoadingExisting(false);
      }
    };

    loadExistingAnalysis();
  }, [property?.id, user?.id, externalAnalysis, isReadOnly]);

  // Load usage stats
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!user?.id) return;
      const stats = await usageTrackingService.getUsageStats(user.id, subscriptionTier);
      setUsageStats(stats);
    };
    loadUsageStats();
  }, [user?.id, subscriptionTier]);

  const generateAnalysis = async (forceRegenerate = false) => {
    if (!property || !user?.id) return;

    // Check AI analysis limit
    const limits = stripeService?.getTierLimits(subscriptionTier);
    if (limits?.aiAnalysesPerMonth !== Infinity && limits?.aiAnalysesPerMonth !== undefined) {
      if (usageStats?.aiAnalyses && usageStats.aiAnalyses.used >= limits.aiAnalysesPerMonth) {
        setError(`Vous avez atteint la limite d'analyses IA ce mois. Passez a Pro pour des analyses illimitees.`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate all metrics first
      const formData = propertyService.toFormFormat(property);
      const metrics = propertyService.calculateMetrics(formData);

      // Calculate additional KPIs
      const totalMonthlyRevenue = property?.total_monthly_revenue || 0;
      const totalMonthlyExpenses = property?.total_monthly_expenses || 0;
      const monthlyCashflow = metrics.monthlyCashflow || 0;
      const annualCashflow = monthlyCashflow * 12;
      const annualNOI = metrics.noi || 0;
      const capRate = metrics.capRate || 0;
      const totalInvestment = metrics.totalInitialInvestment || property?.down_payment_amount || 0;
      const cashOnCashReturn = totalInvestment > 0 ? (annualCashflow / totalInvestment) * 100 : 0;
      // Calculate annual mortgage payment using utility function (handles Canadian semi-annual compounding)
      const mortgageAmount = property?.mortgage_amount || 0;
      const interestRate = property?.interest_rate || 0;
      const amortizationYears = property?.amortization_period || 25;
      const paymentFrequency = property?.payment_frequency || 'monthly';

      const mortgagePaymentCalc = calculateMortgagePayment(
        mortgageAmount,
        interestRate,
        amortizationYears,
        paymentFrequency
      );
      const annualMortgagePayment = mortgagePaymentCalc.annualPayment;
      const dscr = annualMortgagePayment > 0 ? (annualNOI / annualMortgagePayment) : 0;

      // Calculate 10-year ROI
      const appreciationRate = property?.appreciation_rate || 3;
      const rentGrowth = (property?.rent_growth_rate || 2) / 100;
      const expenseGrowth = 0.02;
      const baseRevenueAnnual = totalMonthlyRevenue * 12;
      const baseExpenses = totalMonthlyExpenses * 12;

      const cumulativeCashflow10Years = Array.from({ length: 10 }, (_, j) => {
        const yRevenue = baseRevenueAnnual * Math.pow(1 + rentGrowth, j);
        const yExpenses = baseExpenses * Math.pow(1 + expenseGrowth, j);
        const yNOI = yRevenue - yExpenses;
        // Mortgage payment stops after amortization period
        const yActualMortgagePayment = ((j + 1) > amortizationYears) ? 0 : annualMortgagePayment;
        return yNOI - yActualMortgagePayment;
      }).reduce((sum, cf) => sum + (cf || 0), 0);

      const futureValue = (property?.purchase_price || 0) * Math.pow(1 + appreciationRate / 100, 10);
      const equityGain = futureValue - (property?.purchase_price || 0);
      const roi10Year = totalInvestment > 0
        ? ((cumulativeCashflow10Years + equityGain) / totalInvestment) * 100
        : 0;

      // Validate property data and include validation results
      const { missingFields, warnings } = validatePropertyData(property);
      const validationIssues = {
        critical: missingFields.map(f => f.label),
        warnings: warnings.map(f => f.label)
      };

      // Prepare property data for AI analysis
      const propertyDataForAI = {
        ...property,
        ...metrics,
        totalMonthlyRevenue,
        totalMonthlyExpenses,
        monthlyCashflow,
        annualCashflow,
        noi: annualNOI,
        capRate,
        cashOnCashReturn,
        dscr,
        roi10Year,
        validationIssues // Include validation issues for AI to mention
      };

      const result = await propertyService.analyzePropertyAI(
        propertyDataForAI,
        user.id,
        property.id, // Always pass propertyId so it gets saved and counted
        forceRegenerate, // Pass forceRegenerate flag to create new entry
        userProfile // Pass investor profile for personalized analysis
      );

      if (result?.success) {
        setAnalysis(result.data);
        setIsCached(result.cached || false);

        // Check if save failed (result might have saveError property)
        if (result?.saveError) {
          console.warn('Analysis generated but save failed:', result.saveError);
          setError('L\'analyse a été générée mais n\'a pas pu être sauvegardée. Veuillez réessayer.');
        }

        // Always refresh usage stats after generating analysis (especially important for regeneration)
        // Add a small delay to ensure database has updated
        if (user?.id) {
          setTimeout(async () => {
            const stats = await usageTrackingService.getUsageStats(user.id);
            setUsageStats(stats);
          }, 500); // 500ms delay to allow database to update
        }
      } else {
        setError(result?.error || 'Erreur lors de l\'analyse');
      }
    } catch (err) {
      console.error('Error generating AI analysis:', err);
      setError(err?.message || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getRecommendationColor = (niveau) => {
    const niveauUpper = niveau?.toUpperCase() || '';
    if (niveauUpper.includes('EXCELLENT')) return 'var(--color-success)';
    if (niveauUpper.includes('BON')) return 'var(--color-primary)';
    if (niveauUpper.includes('MOYEN')) return 'var(--color-warning)';
    if (niveauUpper.includes('FAIBLE')) return 'var(--color-destructive)';
    return 'var(--color-muted-foreground)';
  };

  if (!analysis && !loading && !error) {
    return (
      <div className="p-6 md:p-10 text-center bg-card rounded-xl border border-border">
        <Icon name="Sparkles" size={48} className="mb-4 text-primary mx-auto" />
        <h3 className="text-xl font-semibold mb-2 text-foreground">
          Analyse IA de la propriété
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Obtenez une analyse détaillée de votre propriété avec l'intelligence artificielle
        </p>
        <div className="flex flex-col gap-3 items-center">
          {usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit && (
            <div style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              background: 'var(--color-warning)',
              color: 'var(--color-warning-foreground)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              marginBottom: 'var(--spacing-sm)',
              textAlign: 'center'
            }}>
              Limite atteinte : {usageStats.aiAnalyses.used}/{usageStats.aiAnalyses.limit} analyses ce mois
            </div>
          )}
          <Button
            variant="default"
            iconName="Sparkles"
            onClick={() => generateAnalysis(false)}
            disabled={usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit}
          >
            Générer l'analyse IA
          </Button>
          {usageStats?.aiAnalyses && typeof usageStats.aiAnalyses.limit === 'number' && usageStats.aiAnalyses.used >= usageStats.aiAnalyses.limit && (
            <Button
              variant="outline"
              iconName="Crown"
              onClick={() => navigate('/subscription-pricing')}
            >
              Débloquer
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        padding: 'var(--spacing-xl)',
        textAlign: 'center',
        background: 'var(--color-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)'
      }}>
        <Icon name="Loader2" size={48} className="mb-4 animate-spin" style={{ color: 'var(--color-primary)' }} />
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--color-muted-foreground)'
        }}>
          Génération de l'analyse en cours...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: 'var(--spacing-xl)',
        textAlign: 'center',
        background: 'var(--color-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-destructive)'
      }}>
        <Icon name="AlertCircle" size={48} className="mb-4" style={{ color: 'var(--color-destructive)' }} />
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--color-destructive)',
          marginBottom: 'var(--spacing-md)'
        }}>
          {error}
        </p>
        <Button
          variant="outline"
          onClick={() => generateAnalysis(false)}
        >
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--color-card)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: 'var(--spacing-md)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--color-muted)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          <Icon name="Sparkles" size={20} style={{ color: 'var(--color-primary)' }} />
          <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-foreground)',
            margin: 0
          }}>
            Analyse IA
          </h3>
        </div>
        {!isReadOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            {isCached && (
              <span style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '0.75rem',
                color: 'var(--color-muted-foreground)',
                padding: '4px 8px',
                background: 'var(--color-muted)',
                borderRadius: 'var(--radius-sm)'
              }}>
                Enregistré
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="RefreshCw"
              onClick={() => generateAnalysis(true)}
            >
              Régénérer
            </Button>
          </div>
        )}
      </div>

      {/* Résumé exécutif */}
      {analysis?.resume_executif && (
        <div style={{
          padding: 'var(--spacing-md)',
          background: 'var(--color-primary)',
          color: 'var(--color-primary-foreground)'
        }}>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            margin: 0,
            fontWeight: 500
          }}>
            {analysis.resume_executif}
          </p>
        </div>
      )}

      {/* Sections */}
      <div style={{ padding: 'var(--spacing-md)' }}>
        {/* Secteur */}
        {analysis?.secteur && (
          <div style={{
            marginBottom: 'var(--spacing-md)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            position: 'relative'
          }}>
            <button
              onClick={() => toggleSection('secteur')}
              style={{
                width: '100%',
                padding: 'var(--spacing-md)',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                textAlign: 'left',
                borderTopLeftRadius: 'var(--radius-md)',
                borderTopRightRadius: 'var(--radius-md)'
              }}
            >
              <span>Secteur & Localisation</span>
              <Icon
                name={expandedSections.secteur ? "ChevronDown" : "ChevronRight"}
                size={20}
              />
            </button>
            {expandedSections.secteur && (
              <div style={{ padding: '0 var(--spacing-md) var(--spacing-md)' }}>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  {analysis.secteur.analyse}
                </p>
                {analysis.secteur.points_forts && analysis.secteur.points_forts.length > 0 && (
                  <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--color-success)',
                      marginBottom: '4px'
                    }}>
                      Points forts:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {analysis.secteur.points_forts.map((point, idx) => (
                        <li key={idx} style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.875rem',
                          color: 'var(--color-foreground)',
                          marginBottom: '2px'
                        }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.secteur.points_faibles && analysis.secteur.points_faibles.length > 0 && (
                  <div>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--color-destructive)',
                      marginBottom: '4px'
                    }}>
                      Points faibles:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {analysis.secteur.points_faibles.map((point, idx) => (
                        <li key={idx} style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.875rem',
                          color: 'var(--color-foreground)',
                          marginBottom: '2px'
                        }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Prix */}
        {analysis?.prix && (
          <div style={{
            marginBottom: 'var(--spacing-md)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            position: 'relative'
          }}>
            <button
              onClick={() => toggleSection('prix')}
              style={{
                width: '100%',
                padding: 'var(--spacing-md)',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                textAlign: 'left',
                borderTopLeftRadius: 'var(--radius-md)',
                borderTopRightRadius: 'var(--radius-md)'
              }}
            >
              <span>Évaluation du Prix</span>
              <Icon
                name={expandedSections.prix ? "ChevronDown" : "ChevronRight"}
                size={20}
              />
            </button>
            {expandedSections.prix && (
              <div style={{ padding: '0 var(--spacing-md) var(--spacing-md)' }}>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  {analysis.prix.evaluation}
                </p>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'var(--color-foreground)'
                }}>
                  {analysis.prix.justification}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Rentabilité */}
        {analysis?.rentabilite && (
          <div style={{
            marginBottom: 'var(--spacing-md)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            position: 'relative'
          }}>
            <button
              onClick={() => toggleSection('rentabilite')}
              style={{
                width: '100%',
                padding: 'var(--spacing-md)',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                textAlign: 'left',
                borderTopLeftRadius: 'var(--radius-md)',
                borderTopRightRadius: 'var(--radius-md)'
              }}
            >
              <span>Rentabilité</span>
              <Icon
                name={expandedSections.rentabilite ? "ChevronDown" : "ChevronRight"}
                size={20}
              />
            </button>
            {expandedSections.rentabilite && (
              <div style={{ padding: '0 var(--spacing-md) var(--spacing-md)' }}>
                <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)',
                    marginBottom: '4px'
                  }}>
                    Cashflow:
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'var(--color-foreground)'
                  }}>
                    {analysis.rentabilite.cashflow}
                  </p>
                </div>
                <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <div className="flex items-center gap-1 mb-1">
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      margin: 0
                    }}>
                      DSCR:
                    </p>
                    <InfoTooltip content="Le Debt Service Coverage Ratio (DSCR) mesure la capacité de l'immeuble à couvrir ses versements hypothécaires." />
                  </div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'var(--color-foreground)'
                  }}>
                    {analysis.rentabilite.dscr}
                  </p>
                </div>
                <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)',
                    marginBottom: '4px'
                  }}>
                    ROI:
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'var(--color-foreground)'
                  }}>
                    {analysis.rentabilite.roi}
                  </p>
                </div>
                <div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)',
                    marginBottom: '4px'
                  }}>
                    CAP Rate:
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'var(--color-foreground)'
                  }}>
                    {analysis.rentabilite.cap_rate}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommandation */}
        {analysis?.recommandation && (
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            position: 'relative'
          }}>
            <button
              onClick={() => toggleSection('recommandation')}
              style={{
                width: '100%',
                padding: 'var(--spacing-md)',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                textAlign: 'left',
                borderTopLeftRadius: 'var(--radius-md)',
                borderTopRightRadius: 'var(--radius-md)'
              }}
            >
              <span>Recommandation</span>
              <Icon
                name={expandedSections.recommandation ? "ChevronDown" : "ChevronRight"}
                size={20}
              />
            </button>
            {expandedSections.recommandation && (
              <div style={{ padding: '0 var(--spacing-md) var(--spacing-md)' }}>
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: getRecommendationColor(analysis.recommandation.niveau),
                  color: 'white',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  {analysis.recommandation.niveau}
                </div>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  {analysis.recommandation.justification}
                </p>
                {analysis.recommandation.conditions && (
                  <div style={{
                    padding: 'var(--spacing-sm)',
                    background: 'var(--color-muted)',
                    borderRadius: 'var(--radius-sm)',
                    borderLeft: '3px solid var(--color-primary)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--color-foreground)',
                      marginBottom: '4px'
                    }}>
                      Conditions / Améliorations suggérées:
                    </p>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      color: 'var(--color-foreground)',
                      margin: 0
                    }}>
                      {analysis.recommandation.conditions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyAIAnalysis;
